import { computeSystemRecommendation } from '@core/computeSystemRecommendation';
import { generateCostOverTimeChart, generateAnnualCostChart, generateCarbonSavingsChart, buildDecisionScenarios } from '@core/chartData';
import { normalizeSystemRecOutput, type NormalizedOutput } from '@core/normalizeOutput';
import type { SystemRecInput, SystemRecOutput, DecisionScenario } from '@core/types';
import type { CostOverTimeChartData, AnnualCostChartData, CarbonSavingsChartData } from '@core/chartData';
import { validateJsonResponse } from '../utils/responseValidation';

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const USE_API = import.meta.env.VITE_USE_API !== 'false'; // Default to true

// Sample input data for demonstration
export const getSampleInput = (): SystemRecInput => ({
  propertyType: "semi",
  bedrooms: 3,
  bathrooms: 2,
  occupancy: 4,
  hotWaterDemand: "medium",
  drawsPerDay: 8,
  existingSystem: "regular",
  currentBoiler: "regular",
  currentWater: "open_vented",
  mainsPressure: 2.5,
  flowRate: 18,
  heatLossKw: 12,
  gasAvailable: true,
  electricalCapacityConfidence: "likely_ok",
  disruptionTolerance: "moderate",
  budgetBand: "medium",
  costPriority: "balanced",
  budgetVsLongevity: "balanced",
  budgetVsGreen: "balanced",
  futureDemand: "stable",
  temporaryGuests: "occasional",
  waterHardness: "moderate",
  existingSystemAge: 15,
  urgency: "soon",
  numRecommendations: 3,
  spaceAvailability: "medium",
  temperaturePreference: "comfort",
  solarPanels: "none",
  heatEmitterType: "standard_rads",
  acceptsVisiblePipework: false,
  wallSpaceForOversizedRads: false,
  insulationLevel: "medium",
  plannedInsulationUpgrades: false,
  baselineEnabled: true,
  currentSystemEfficiency: {
    source: 'ASSUMED',
    value: 0.75, // 75% efficiency (decimal form)
  },
  currentAnnualGasCost: 1200,
  currentAnnualElectricityCost: 800,
  maintenanceHistoryYears: 5,
  serviceVisitsPerYear: 1,
  avgRepairCostPerYear: 150,
});

export interface DecisionEngineResult {
  output: SystemRecOutput;
  normalizedOutput: NormalizedOutput;  // NEW: Single source of truth for UI/export
  scenarios: DecisionScenario[];  // NEW: Unified scenario list for UI
  costOverTimeData: CostOverTimeChartData | null;
  annualCostData: AnnualCostChartData | null;
  carbonSavingsData: CarbonSavingsChartData | null;
  /** The actual input passed to computeSystemRecommendation - for debug bundle consistency */
  normalizedInput: SystemRecInput;
}

/**
 * Call the API to get recommendation
 */
async function callRecommendationAPI(input: SystemRecInput): Promise<SystemRecOutput> {
  const response = await fetch(`${API_BASE_URL}/recommend`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });

  // Validate Content-Type
  validateJsonResponse(response);

  // Now we know it's JSON, safe to parse
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(`API Error: ${error.error || response.statusText}`);
  }

  return response.json();
}

/**
 * Generate a stable SHA-256 hash of input payload for logging/debugging
 * This is for duplicate detection only - does NOT block on collisions
 */
async function hashInputPayload(input: SystemRecInput): Promise<string> {
  const inputJson = JSON.stringify(input);
  const encoder = new TextEncoder();
  const data = encoder.encode(inputJson);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  // Convert to base64url for compact display
  const base64 = btoa(String.fromCharCode(...hashArray))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
  return base64;
}

/**
 * Validate that the input contains real data (not defaults or empty values)
 * This validates SHAPE, not completion - only requires minimal fields to run engine
 * Skip-friendly: does not require all optional fields to be populated
 */
async function validateInputPayload(input: SystemRecInput): Promise<void> {
  // Validate shape: only require minimal fields needed to run the engine
  // These are fields that the engine cannot function without
  const requiredFields = [
    'propertyType',
    'bedrooms',
    'bathrooms',
  ];

  const missingFields = requiredFields.filter(field => {
    const value = input[field as keyof SystemRecInput];
    return value === undefined || value === null || value === '';
  });

  if (missingFields.length > 0) {
    const error = `❌ VALIDATION FAILED: Missing required fields: ${missingFields.join(', ')}`;
    console.error(error);
    console.error('Input payload:', input);
    throw new Error(`Invalid input: ${missingFields.join(', ')} must be provided`);
  }

  // Check if this looks like default/sample data by comparing key fields
  const isSampleData = 
    input.propertyType === 'semi' &&
    input.bedrooms === 3 &&
    input.bathrooms === 2 &&
    input.occupancy === 4 &&
    input.hotWaterDemand === 'medium';

  if (isSampleData) {
    console.warn('⚠️ WARNING: Input appears to be sample/default data. Real user input may not be reaching the engine.');
  }

  // Log input hash for duplicate detection (log-only, does not block)
  // Using SHA-256 for stable, collision-resistant hashing
  const hash = await hashInputPayload(input);
  console.log(`✅ Input validation passed. Hash: ${hash.substring(0, 16)}...`);
}

/**
 * Run decision engine locally (in-browser)
 */
async function runDecisionEngineLocal(input: SystemRecInput): Promise<DecisionEngineResult> {
  // HARD ASSERTION: Validate input before processing
  console.log('🔍 Validating input payload...');
  await validateInputPayload(input);
  console.log('📊 Running recommendation engine with input:', {
    propertyType: input.propertyType,
    bedrooms: input.bedrooms,
    bathrooms: input.bathrooms,
    occupancy: input.occupancy,
    hotWaterDemand: input.hotWaterDemand,
    currentBoiler: input.currentBoiler,
    currentWater: input.currentWater,
  });

  const output = computeSystemRecommendation(input);

  // HARD ASSERTION: Check if output contains valid recommendations
  if (!output.primary && (!output.alternatives || output.alternatives.length === 0)) {
    console.error('❌ ENGINE FAILURE: No recommendations generated');
    console.error('Input:', input);
    console.error('Output:', output);
    throw new Error('Recommendation engine failed to generate any options');
  }

  console.log('✅ Engine generated recommendations:', {
    primary: output.primary?.title,
    alternativeCount: output.alternatives?.length || 0,
    consideredCount: output.consideredOptions?.length || 0,
  });

  // NORMALIZATION LAYER - Single source of truth
  const normalizedOutput = normalizeSystemRecOutput(output, input);

  // Build unified decision scenarios from output
  const scenarios = buildDecisionScenarios(output);

  // Generate chart data for the recommendations
  const costOverTimeData = generateCostOverTimeChart(output);
  const annualCostData = generateAnnualCostChart(output);
  const carbonSavingsData = generateCarbonSavingsChart(output);

  return {
    output,
    normalizedOutput,
    scenarios,
    costOverTimeData,
    annualCostData,
    carbonSavingsData,
    normalizedInput: input,  // Store for debug bundle consistency
  };
}

/**
 * Run decision engine (API or local based on configuration)
 */
export const runDecisionEngine = async (input: SystemRecInput): Promise<DecisionEngineResult> => {
  // HARD ASSERTION: Validate input at entry point (before API or local execution)
  console.log('🔍 Validating input payload at entry point...');
  await validateInputPayload(input);

  if (USE_API) {
    try {
      console.log('📡 Calling recommendation API...');
      const output = await callRecommendationAPI(input);

      // HARD ASSERTION: Check if output contains valid recommendations
      if (!output.primary && (!output.alternatives || output.alternatives.length === 0)) {
        console.error('❌ API RETURNED NO RECOMMENDATIONS');
        console.error('Input:', input);
        console.error('Output:', output);
        throw new Error('API failed to generate any options');
      }

      console.log('✅ API returned recommendations:', {
        primary: output.primary?.title,
        alternativeCount: output.alternatives?.length || 0,
      });

      // NORMALIZATION LAYER - Single source of truth
      const normalizedOutput = normalizeSystemRecOutput(output, input);

      // Build unified decision scenarios from output
      const scenarios = buildDecisionScenarios(output);

      // Generate chart data locally from the API response
      const costOverTimeData = generateCostOverTimeChart(output);
      const annualCostData = generateAnnualCostChart(output);
      const carbonSavingsData = generateCarbonSavingsChart(output);

      return {
        output,
        normalizedOutput,
        scenarios,
        costOverTimeData,
        annualCostData,
        carbonSavingsData,
        normalizedInput: input,  // Store for debug bundle consistency
      };
    } catch (error) {
      console.error('API call failed, falling back to local execution:', error);
      // Fallback to local execution if API fails
      return runDecisionEngineLocal(input);
    }
  } else {
    // Use local in-browser execution
    return runDecisionEngineLocal(input);
  }
};

// Run with sample data (for demo purposes)
export const getSampleDecisionResult = async (): Promise<DecisionEngineResult> => {
  const sampleInput = getSampleInput();
  return runDecisionEngine(sampleInput);
};
